import { useState } from 'react'
import { type Employee, EmployeePicker } from '@/entities/employee'
import type { Excursion } from '@/entities/excursion'
import {
  type Task,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useUpdateTaskMutation,
} from '@/entities/task'
import { errorMessage, type Warning } from '@/shared/api'
import type { Interval } from '@/shared/lib'
import { fmtInterval } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { IntervalField } from '@/shared/ui/interval-field'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'
import { defaultSlot } from '../lib/slot'

export interface TaskDialogState {
  /** Правим существующее дело либо заводим новое из списка дел. */
  task: Task | null
  slot: Interval | null
  assigneeIds: string[]
  excursionId: string | null
}

interface TaskDialogProps {
  state: TaskDialogState | null
  employees: Employee[]
  excursions: Excursion[]
  /** Опорный день экрана: от него отсчитывается время, когда его назначают в карточке. */
  day: Date
  onClose: () => void
}

function warningText(warnings: Warning[], employees: Employee[]): string | null {
  if (warnings.length === 0) return null
  const names = [...new Set(warnings.map((w) => w.employeeId))]
    .map((id) => employees.find((e) => e.id === id)?.name ?? 'кто-то')
    .join(', ')
  return `Пересечение у ${names}: ${warnings.map((w) => w.title).join(', ')}`
}

/**
 * Карточка блока. Пересечения показываются после сохранения предупреждением —
 * наложение допустимо (`ConflictWarning`), продукт не мешает договориться.
 */
export function TaskDialog({ state, employees, excursions, day, onClose }: TaskDialogProps) {
  const create = useCreateTaskMutation()
  const update = useUpdateTaskMutation()
  const remove = useDeleteTaskMutation()

  const task = state?.task ?? null
  const [title, setTitle] = useState(task?.title ?? '')
  const [note, setNote] = useState(task?.note ?? '')
  const [slot, setSlot] = useState<Interval | null>(task?.slot ?? state?.slot ?? null)
  const [assigneeIds, setAssigneeIds] = useState<string[]>(
    task?.assigneeIds ?? state?.assigneeIds ?? [],
  )
  const [excursionId, setExcursionId] = useState<string | null>(
    task?.excursionId ?? state?.excursionId ?? null,
  )
  const [key, setKey] = useState('')

  // Диалог переиспользуется между разными блоками — при смене цели поля
  // перезаполняются из новой цели, а не тянут прежний ввод.
  const nextKey = `${state?.task?.id ?? 'new'}:${state?.slot?.startsAt.toISOString() ?? ''}`
  if (state && key !== nextKey) {
    setKey(nextKey)
    setTitle(task?.title ?? '')
    setNote(task?.note ?? '')
    setSlot(task?.slot ?? state.slot)
    setAssigneeIds(task?.assigneeIds ?? state.assigneeIds)
    setExcursionId(task?.excursionId ?? state.excursionId)
  }

  const report = (warnings: Warning[]) => {
    const text = warningText(warnings, employees)
    if (text) toast.notify(text)
    onClose()
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const payload = { title: title.trim(), slot, assigneeIds, excursionId, note: note.trim() }
    if (task) {
      update.mutate(
        { id: task.id, patch: payload },
        { onSuccess: (result) => report(result.warnings) },
      )
    } else {
      create.mutate(payload, { onSuccess: (result) => report(result.warnings) })
    }
  }

  const pending = create.isPending || update.isPending
  const error = create.error ?? update.error ?? remove.error

  return (
    <Dialog
      open={state !== null}
      onClose={onClose}
      title={task ? 'Дело' : 'Новое дело'}
      size="lg"
      footer={
        <>
          {task && (
            <Button
              variant="danger"
              onClick={() => remove.mutate(task.id, { onSuccess: onClose })}
              disabled={remove.isPending}
            >
              Удалить
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" form="task-form" disabled={pending || title.trim().length === 0}>
            Сохранить
          </Button>
        </>
      }
    >
      <form id="task-form" onSubmit={submit} className="flex flex-col gap-5">
        <Field label="Что сделать">
          {(props) => (
            <Input {...props} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          )}
        </Field>

        {slot ? (
          <div className="flex flex-col gap-2">
            <IntervalField value={slot} onChange={setSlot} />
            <button
              type="button"
              onClick={() => setSlot(null)}
              className="self-start text-xs text-text-tertiary underline hover:text-text-primary"
            >
              Снять время — дело уйдёт в список
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-text-tertiary">
              Без времени: дело живёт списком и в сетку не попадает.
            </p>
            <Button
              variant="secondary"
              size="sm"
              className="self-start"
              onClick={() => setSlot(defaultSlot(day))}
            >
              Назначить время
            </Button>
          </div>
        )}

        <EmployeePicker
          label="На кого"
          employees={employees}
          selected={assigneeIds}
          onChange={setAssigneeIds}
          hint="Можно никого — время будет занято, но ничьё"
        />

        <Field label="К какому выходу">
          {(props) => (
            <Select
              {...props}
              value={excursionId ?? ''}
              onChange={(value) => setExcursionId(value || null)}
              placeholder="Не привязано"
              options={[
                { value: '', label: 'Не привязано' },
                ...excursions.map((excursion) => ({
                  value: excursion.id,
                  label: `${excursion.title} · ${fmtInterval(excursion.slot)}`,
                })),
              ]}
            />
          )}
        </Field>

        <Field label="Заметка">
          {(props) => (
            <Textarea {...props} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </Field>

        {task && (
          <Checkbox
            checked={task.done}
            onChange={() =>
              update.mutate({ id: task.id, patch: { done: !task.done } }, { onSuccess: onClose })
            }
            label={<span className="text-sm">Сделано</span>}
          />
        )}

        {error && (
          <p role="alert" className="text-sm text-accent">
            {errorMessage(error)}
          </p>
        )}
      </form>
    </Dialog>
  )
}
